import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PaperAirplaneIcon,
  PencilSquareIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table';
import { Input } from '../../../../components/ui/input';
import { useDialogStore } from '../../../../store/dialogStore';
import {
  TournamentAdminService,
  isDuplicateRequest,
} from '../TournamentAdminService';
import SendToPccDialog from './SendToPccDialog';
import {
  TournamentOverviewClub,
  TournamentOverviewRow,
} from '../TournamentAdmin.types';

const TABLE_HEADER: Array<{ key: string; text: string }> = [
  { key: 'date', text: '📅 Datum' },
  { key: 'tournament', text: '🏆 Turnier' },
  { key: 'deadline', text: '⏰ Meldeschluss' },
  { key: 'action', text: '⚙️ Aktion' },
];

type RegistrationState = 'open' | 'not_yet_open' | 'closed';

const STATUS_BADGE: Record<RegistrationState, string> = {
  open: 'text-green-600 border-green-200 bg-green-50',
  not_yet_open: 'text-amber-700 border-amber-200 bg-amber-50',
  closed: 'text-gray-600 border-gray-200 bg-gray-50',
};

const STATUS_LABEL: Record<RegistrationState, string> = {
  open: 'Meldung offen',
  not_yet_open: 'Noch nicht geöffnet',
  closed: 'Meldeschluss vorbei',
};

// "dd.mm.yyyy[ HH:MM]" -> Date, for the filter and the status badge
const parseGermanDateTime = (value: string): Date | null => {
  const m = value.match(/^(\d{2})\.(\d{2})\.(\d{4})(?: (\d{2}):(\d{2}))?/);
  if (!m) return null;
  return new Date(
    Number(m[3]),
    Number(m[2]) - 1,
    Number(m[1]),
    Number(m[4] ?? 0),
    Number(m[5] ?? 0),
  );
};

const registrationState = (
  row: TournamentOverviewRow,
  now: number,
): RegistrationState => {
  const start = parseGermanDateTime(row.registrationStart)?.getTime();
  const end = parseGermanDateTime(row.registrationEnd)?.getTime();
  if (start !== undefined && now < start) return 'not_yet_open';
  if (end !== undefined && now > end) return 'closed';
  return 'open';
};

// "4 Spieler + 2 Ersatz", or a warning when the club filed a Meldung without any players
const squadLabel = (club: TournamentOverviewClub) => {
  if (club.players === 0 && club.substitutes === 0) return '(ohne Spieler)';
  const parts = [`${club.players} Spieler`];
  if (club.substitutes > 0) parts.push(`${club.substitutes} Ersatz`);
  return `(${parts.join(' + ')})`;
};

const ClubEntry: React.FC<{ club: TournamentOverviewClub }> = ({ club }) => {
  const content = (
    <>
      {club.registered ? (
        <CheckCircleIcon className="w-3.5 h-3.5 shrink-0 text-green-600" />
      ) : (
        <ClockIcon className="w-3.5 h-3.5 shrink-0" />
      )}
      <span className={club.registered ? 'font-medium' : ''}>
        {club.name ?? club.clubNumberCountry}
      </span>
      {club.registered && (
        <span className="text-gray-500">{squadLabel(club)}</span>
      )}
    </>
  );

  const className = `inline-flex items-center gap-1 text-xs whitespace-nowrap ${
    club.registered ? 'text-gray-800' : 'text-red-600'
  }`;

  // A registered club's name opens its Meldebestätigung when the PDF is still on disk.
  if (club.pdfUrl) {
    return (
      <a
        href={club.pdfUrl}
        target="_blank"
        rel="noopener noreferrer"
        title={`${club.clubNumberCountry}: Meldebestätigung (PDF) öffnen`}
        className={`${className} hover:underline hover:text-[#52A445]`}
      >
        {content}
      </a>
    );
  }

  return (
    <span
      title={
        club.registered
          ? `${club.clubNumberCountry}: Meldung liegt vor`
          : `${club.clubNumberCountry}: noch keine Meldung`
      }
      className={className}
    >
      {content}
    </span>
  );
};

const TournamentAdminList: React.FC = () => {
  const [rows, setRows] = useState<TournamentOverviewRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const navigate = useNavigate();
  const openDialog = useDialogStore((state) => state.openDialog);
  const closeDialog = useDialogStore((state) => state.closeDialog);
  const now = Date.now();

  useEffect(() => {
    const load = async () => {
      try {
        setRows(await TournamentAdminService.fetchOverview());
        setIsLoading(false);
      } catch (error) {
        if (isDuplicateRequest(error)) return;
        console.error('Turnierliste konnte nicht geladen werden:', error);
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const filteredRows = useMemo(() => {
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;
    const term = searchTerm.toLowerCase();

    return rows.filter((row) => {
      const haystack = [
        row.title,
        row.venue ?? '',
        ...row.clubs.map((c) => `${c.name ?? ''} ${c.clubNumberCountry}`),
      ]
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(term)) return false;
      if (!start && !end) return true;
      const playedOn = parseGermanDateTime(row.startDate);
      if (!playedOn) return false;
      if (start && playedOn < start) return false;
      if (end && playedOn > end) return false;
      return true;
    });
  }, [rows, searchTerm, startDate, endDate]);

  const sendToPcc = (row: TournamentOverviewRow) => {
    openDialog(<SendToPccDialog tournament={row} />, closeDialog, true);
  };

  return (
    <div className="bg-white border rounded-md p-4 mx-5">
      <div className="flex justify-between items-center mb-4 gap-4">
        <div className="font-medium text-lg">Ihre Meldungsübersicht</div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-500 font-medium whitespace-nowrap">
            Gesamt: {filteredRows.length}
          </div>
          <button
            type="button"
            onClick={() => navigate('/association/tournaments/new')}
            className="flex items-center gap-1 text-sm font-bold rounded-md py-2 px-4 text-white bg-[#52A445] hover:bg-green-700"
          >
            <PlusIcon className="w-4 h-4" />
            Neues Turnier anlegen
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Nach Turnier, Austragungsort oder Club suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex items-center gap-2 flex-1">
            <span className="text-sm text-gray-600">Von:</span>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full"
            />
          </div>
          <div className="flex items-center gap-2 flex-1">
            <span className="text-sm text-gray-600">Bis:</span>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full"
            />
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="text-center py-10 text-gray-500">
          <ArrowPathIcon className="w-10 h-10 mx-auto mb-4 animate-spin" />
        </div>
      )}

      {!isLoading && filteredRows.length === 0 && (
        <div className="text-center py-10 text-gray-500">
          <ExclamationTriangleIcon className="w-10 h-10 mx-auto mb-4" />
          <b>Keine Turniere gefunden.</b>
          {searchTerm && (
            <div className="text-xs text-gray-500 mt-2">
              Für "
              <span className="font-semibold text-gray-700">{searchTerm}</span>"
              wurden keine Ergebnisse gefunden.
            </div>
          )}
        </div>
      )}

      {!isLoading && filteredRows.length > 0 && (
        <Table>
          <TableCaption>Turniere des Verbands im laufenden Jahr</TableCaption>
          <TableHeader>
            <TableRow>
              {TABLE_HEADER.map((header) => (
                <TableHead className="whitespace-nowrap" key={header.key}>
                  {header.text}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRows.map((row) => {
              const state = registrationState(row, now);
              const registeredCount = row.clubs.filter(
                (c) => c.registered,
              ).length;
              return (
                <TableRow key={row.id}>
                  <TableCell className="whitespace-nowrap align-top">
                    <div className="font-medium">{row.startDate}</div>
                    {row.endDate !== row.startDate && (
                      <div className="text-xs text-gray-500">
                        bis {row.endDate}
                      </div>
                    )}
                    <div className="text-[10px] text-gray-400 mt-1">
                      #{row.id}
                    </div>
                  </TableCell>
                  <TableCell className="align-top">
                    <div className="font-bold">{row.title}</div>
                    <div className="flex items-center gap-1 text-xs text-gray-600">
                      <MapPinIcon className="w-3.5 h-3.5 shrink-0" />
                      {row.venue ?? 'Austragungsort unbekannt'}
                    </div>
                    {row.clubs.length > 0 && (
                      <div className="mt-2">
                        <div className="text-[11px] text-gray-500 mb-1">
                          Teilnehmer: {registeredCount} / {row.clubs.length}{' '}
                          gemeldet
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1">
                          {row.clubs.map((club) => (
                            <ClubEntry key={club.clubId} club={club} />
                          ))}
                        </div>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap align-top">
                    <div className="text-sm">{row.registrationEnd}</div>
                    <div
                      className={`inline-flex whitespace-nowrap items-center text-xs font-medium border rounded-full px-2 py-1 mt-1 ${STATUS_BADGE[state]}`}
                    >
                      {STATUS_LABEL[state]}
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap align-top">
                    <div className="flex flex-col items-start gap-1 text-sm font-bold">
                      <button
                        type="button"
                        onClick={() =>
                          navigate(`/association/tournaments/${row.id}`)
                        }
                        className="flex items-center gap-1 text-[#52A445] hover:underline"
                      >
                        <PencilSquareIcon className="w-4 h-4" />
                        Bearbeiten
                      </button>
                      <button
                        type="button"
                        onClick={() => sendToPcc(row)}
                        className="flex items-center gap-1 text-blue-600 hover:underline"
                      >
                        <PaperAirplaneIcon className="w-4 h-4" />
                        An PCC senden
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default TournamentAdminList;
